import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { AllocatedRegimen } from '../../../../app-constant-types';
import { Subscription } from 'rxjs';
import { CartService } from '@services/cart-service/cart.service';
import { EventLoggerService } from '@services/event-logger-service';
import { RegimenService } from '@services/regimen-service';
import { ShopService } from '@services/shop-service/shop.service';
import { AppConfig } from 'client/app/app.config';
import { fromSelectors } from '@store/selectors';
import { LockedProductBottomsheetComponent } from '@shared/bottom-sheet-layouts/locked-product-bottomsheet/locked-product-bottomsheet.component';
import { CsIconModule } from '@components/icon';
import { ProductComponent } from '../../../../shop/components/product/product.component';

@Component({
  selector: 'acq-shop-products-banner',
  templateUrl: './acq-shop-products-banner.component.html',
  standalone: true,
  imports: [CsIconModule, ProductComponent],
})
export class AcqShopProductsBannerComponent implements OnInit, OnDestroy {
  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly cartService = inject(CartService);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly regimenService = inject(RegimenService);
  private readonly shopService = inject(ShopService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  private readonly subscriptions: Subscription[] = [];
  regimens: AllocatedRegimen[] = [];
  regimenImage: AllocatedRegimen['regimenCoverImage'] = '';
  products: Array<Catalog & { isLocked?: boolean }> = [];
  productsInCart: { [key: string]: number } = {};
  availableProductIds: string[] = [];
  lockedProductIds: string[] = [];
  private regimenProductMap = new Map<string, AllocatedRegimen['class']>();
  private cartItems: Array<{ productId: string; quantity: number }> = [];
  private isCartOperationPending: boolean = false;

  constructor() {
    this.subscriptions.push(
      this.store.select(fromSelectors.selectCartProducts).subscribe((products: { [key: string]: number }) => {
        this.productsInCart = { ...products };
      }),
    );
  }

  async ngOnInit(): Promise<void> {
    this.regimens = await this.regimenService.getRegimens();
    this.regimenImage = this.regimens[0]?.regimenCoverImage ?? '';
    const shopData = this.shopService.getRegimenProductsWithLockState(this.regimens);
    this.products = shopData.products;
    this.availableProductIds = shopData.availableProductIds;
    this.lockedProductIds = shopData.lockedProductIds;
    this.regimenProductMap = shopData.regimenProductMap;
    this.cartItems = await this.cartService.loadCartProductsIntoStore();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async onAddToCart(event: { product: Catalog; image: HTMLImageElement }): Promise<void> {
    await this.updateCart(event.product, this.appConfig.Shared.Cart.Action.ADD, event.image);
  }

  async onRemoveFromCart(product: Catalog): Promise<void> {
    await this.updateCart(product, this.appConfig.Shared.Cart.Action.REMOVE);
  }

  onGotoProduct(product: Catalog): void {
    const sheetData = this.shopService.buildLockedProductSheetData(
      product, this.regimens, this.availableProductIds, this.regimenProductMap, 'acquisition_shop_products_banner',
    );
    if (!sheetData) return;

    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'locked-product-bottomsheet',
      source: sheetData.source,
      type: sheetData.type,
      regimenClass: sheetData.regimenClass,
      productId: product?.objectId,
    }));
    this.bottomSheet.open(LockedProductBottomsheetComponent, { data: sheetData });
  }

  navigateToShop(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'acquisition_shop_products_banner' }));
    this.router.navigateByUrl('/unpaid-user-shop');
  }

  private async updateCart(product: Catalog, action: string, image?: HTMLImageElement): Promise<void> {
    if (this.isCartOperationPending) return;
    this.isCartOperationPending = true;

    try {
      const result = await this.cartService.forceUpdateProductInCart(product, action, {
        img: image,
        productsInCart: this.productsInCart,
        cartItems: this.cartItems,
        section: 'acqShopHomeBanner',
        flyToSelector: '#home-cart-icon',
      });

      if (result.success) {
        this.cartItems = await this.cartService.loadCartProductsIntoStore();
      }
    } finally {
      this.isCartOperationPending = false;
    }
  }
}
